'use client';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Accordion from '@/components/ui/Accordion';
import Footer from '@/components/common/Footer';
import StickyCTA from '@/components/ui/StickyCTA';

export default function GrowthPartnerLander() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));

    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 800);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      observer?.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const faqItems = [
    {
      id: 'faq-1',
      title: 'How much capacity do I get per month?',
      content: 'Growth Partner includes higher monthly capacity than Execution Coverage (40h). Exact hours depend on your needs, but most Growth Partner clients get 60-80 hours per month. We customize based on your goals.'
    },
    {
      id: 'faq-2',
      title: 'What does "priority scheduling" mean?',
      content: 'You get first access to our calendar for shoots, strategy sessions, and project kickoffs. If you need something done this week, you\'re at the front of the line.'
    },
    {
      id: 'faq-3',
      title: 'What\'s the difference between a dedicated lead and a dedicated team?',
      content: 'Execution Coverage gives you one senior strategist. Growth Partner gives you a team: strategist, designer, copywriter, and specialist (ads, email, etc.) based on your channels.'
    },
    {
      id: 'faq-4',
      title: 'Can I start with Execution Coverage and upgrade later?',
      content: 'Yes. Most clients start with Execution Coverage to test the relationship, then upgrade to Growth Partner when they\'re ready to scale.'
    }
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <AppImage 
              src="/assets/images/boc_logo_240x90_white-1771394304209.png"
              alt="Best Odds Corp Logo"
              width={180}
              height={68}
              className="cursor-pointer"
            />
          </Link>
          <Link href="/cart">
            <Button variant="primary" size="md" className="border-glow">
              Get Started - $10,000/mo
            </Button>
          </Link>
        </div>
      </nav>

      {showStickyCTA && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-md border-t border-border py-4 px-6">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div>
              <p className="text-h5">Growth Partner</p>
              <p className="text-body-sm text-muted-foreground">$10,000/mo • Priority + Higher Capacity</p>
            </div>
            <Link href="/cart">
              <Button variant="primary" size="lg" className="border-glow">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      )}

      <StickyCTA
        title="Growth Partner"
        subtitle="$10,000/mo • Priority + Higher Capacity"
        ctaText="Get Started"
        ctaHref="/cart"
        showAfterScroll={800}
      />

      <main className="min-h-screen bg-background text-foreground pt-24">
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto text-center">
            <div className="reveal">
              <Badge variant="primary" className="mb-6">For Brands Ready to Scale</Badge>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
                Your dedicated
                <br />
                <span className="text-primary">growth team</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-12 leading-relaxed">
                Priority access. Higher capacity. Multi-channel campaigns. A full team, not just one person.
              </p>
              <Link href="/cart">
                <Button variant="primary" size="lg" className="border-glow-strong">
                  Become a Growth Partner - $10,000/mo
                </Button>
              </Link>
              <p className="text-body-sm text-muted-foreground mt-4">
                Limited to 8 active partners at a time.
              </p>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="reveal p-8 border border-primary bg-surface-1">
                <h3 className="text-h3 mb-6">This is for you if:</h3>
                <ul className="space-y-4 text-body-sm">
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You're spending $20k+/mo on marketing and need better execution</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You need priority access to our team and calendar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You're running multi-channel campaigns (4+ channels)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You want a dedicated team, not just one account lead</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>You need strategic partnership, not just execution</span>
                  </li>
                </ul>
              </div>
              <div className="reveal p-8 border border-border bg-surface-1">
                <h3 className="text-h3 mb-6">This is NOT for you if:</h3>
                <ul className="space-y-4 text-body-sm text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You're just starting out (try Execution Coverage first)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You only need 1-2 channels managed (Execution Coverage is enough)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You want to learn and execute yourself (try AutoPilot)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground text-xl">✗</span>
                    <span>You're not ready to invest $10k/mo in marketing execution</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                What's Included
              </span>
              <h2 className="text-h2 mb-4">Everything in Execution Coverage, Plus:</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Priority Scheduling</h4>
                <p className="text-body-sm text-muted-foreground">
                  First access to our calendar for shoots, strategy sessions, and project kickoffs.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Higher Monthly Capacity</h4>
                <p className="text-body-sm text-muted-foreground">
                  60-80 hours per month (vs 40h in Execution Coverage). Customized to your needs.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Multi-Channel Campaigns</h4>
                <p className="text-body-sm text-muted-foreground">
                  Run 4+ channels simultaneously with coordinated messaging and creative.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Dedicated Team</h4>
                <p className="text-body-sm text-muted-foreground">
                  Strategist, designer, copywriter, and channel specialists. Not just one person.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Bi-Weekly Strategy Sessions</h4>
                <p className="text-body-sm text-muted-foreground">
                  60-minute deep dives into performance, optimization, and planning.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ 24-Hour Response Time</h4>
                <p className="text-body-sm text-muted-foreground">
                  Priority Slack/email support. We respond within 24 hours, guaranteed.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Quarterly Business Reviews</h4>
                <p className="text-body-sm text-muted-foreground">
                  Comprehensive performance reviews with leadership and strategic planning.
                </p>
              </div>
              <div className="reveal p-6 border border-border bg-background">
                <h4 className="text-h5 mb-3">✓ Custom Reporting</h4>
                <p className="text-body-sm text-muted-foreground">
                  Tailored dashboards and reports for your specific KPIs and stakeholders.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Timeline
              </span>
              <h2 className="text-h2 mb-4">First 90 Days</h2>
            </div>
            <div className="space-y-8">
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">30</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Days 1-30: Foundation & Team Onboarding</h4>
                  <p className="text-body-sm text-muted-foreground">
                    Full audit, team introductions, strategy alignment, and execution kickoff across 3-4 channels.
                  </p>
                </div>
              </div>
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">60</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Days 31-60: Scale & Optimize</h4>
                  <p className="text-body-sm text-muted-foreground">
                    Expand to all channels, run experiments, optimize campaigns, and increase spend where ROI is proven.
                  </p>
                </div>
              </div>
              <div className="reveal flex gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 border border-primary flex items-center justify-center">
                  <span className="text-primary text-h4 font-bold">90</span>
                </div>
                <div>
                  <h4 className="text-h5 mb-2">Days 61-90: Full Execution + Strategic Planning</h4>
                  <p className="text-body-sm text-muted-foreground">
                    All channels running at scale, quarterly business review, and strategic plan for next 90 days.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Results
              </span>
              <h2 className="text-h2 mb-4">What Growth Partners Achieve</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="reveal p-8 border border-border bg-background">
                <p className="text-body-sm text-muted-foreground mb-6">
                  "We scaled from $30k/mo to $120k/mo in ad spend with better ROAS. The Growth Partner team handled everything."
                </p>
                <p className="text-body-sm font-semibold">— Rachel P., E-commerce</p>
              </div>
              <div className="reveal p-8 border border-border bg-background">
                <p className="text-body-sm text-muted-foreground mb-6">
                  "Best marketing investment we've made. It's like having a CMO, creative director, and execution team for $10k/mo."
                </p>
                <p className="text-body-sm font-semibold">— James H., SaaS</p>
              </div>
              <div className="reveal p-8 border border-border bg-background">
                <p className="text-body-sm text-muted-foreground mb-6">
                  "We've been Growth Partners for 2 years. They know our business better than most of our employees."
                </p>
                <p className="text-body-sm font-semibold">— Maria S., B2B Services</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-micro uppercase tracking-ultra-wide text-primary font-medium mb-4 block">
                Questions?
              </span>
              <h2 className="text-h2 mb-4">Common Questions</h2>
            </div>
            <div className="max-w-3xl mx-auto reveal">
              <Accordion items={faqItems} allowMultiple={true} />
            </div>
          </div>
        </section>

        <section className="py-24 px-6">
          <div className="max-w-[1200px] mx-auto text-center">
            <div className="reveal">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Become a Growth Partner.
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                $10,000/mo. Priority access. Dedicated team. Limited to 8 active partners.
              </p>
              <Link href="/cart">
                <Button variant="primary" size="lg" className="border-glow-strong">
                  Apply Now
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Related Services Section */}
      <section className="py-24 px-6 border-t border-border">
        <div className="max-w-[1200px] mx-auto">
          <div className="reveal mb-12">
            <h2 className="text-3xl font-bold mb-4">Explore Our Services</h2>
            <p className="text-body text-muted-foreground">See what's included in Growth Partner</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link href="/services/paid-media-management" className="reveal p-6 border border-border hover:border-primary transition-colors group">
              <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">Paid Media</h3>
              <p className="text-body-sm text-muted-foreground">Scale profitably with data-driven campaigns</p>
            </Link>
            <Link href="/services/content-production" className="reveal p-6 border border-border hover:border-primary transition-colors group">
              <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">Content Production</h3>
              <p className="text-body-sm text-muted-foreground">Professional creative assets</p>
            </Link>
            <Link href="/pricing" className="reveal p-6 border border-border hover:border-primary transition-colors group">
              <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">View All Plans</h3>
              <p className="text-body-sm text-muted-foreground">Compare pricing options</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}